import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api } from '@/lib/api'
import {
  asConversations,
  mergeOpsRoom,
  OPS_READ_EVENT,
  opsActivity,
  seatActivity,
  type Room,
  type SeatConversation,
} from '@/lib/rooms'

const POLL_MS = 20_000

export function useTeamActivity() {
  const [conversations, setConversations] = useState<SeatConversation[]>([])
  const [rooms, setRooms] = useState<Room[]>([])
  const [suppressKey, setSuppressKey] = useState<string | null>(null)

  const load = useCallback(async () => {
    const [convRaw, roomList, opsRoom] = await Promise.all([
      api.getConversations().then((result) => asConversations(result)).catch(() => [] as SeatConversation[]),
      api.listRooms().catch(() => [] as Room[]),
      api.getOpsRoom().catch(() => null),
    ])
    setConversations(convRaw)
    setRooms(mergeOpsRoom(roomList, opsRoom))
  }, [])

  const ops = useMemo(() => opsActivity(rooms, conversations), [rooms, conversations])
  const opsToken = `${ops.unread}|${ops.lastActivityAt ?? ''}|${ops.preview ?? ''}`
  const opsTokenRef = useRef(opsToken)
  opsTokenRef.current = opsToken

  useEffect(() => {
    let alive = true
    const run = () => {
      if (alive) void load()
    }
    run()
    const timer = window.setInterval(run, POLL_MS)
    const onVisible = () => {
      if (document.visibilityState === 'visible') run()
    }
    const onRead = () => setSuppressKey(opsTokenRef.current)
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener(OPS_READ_EVENT, onRead)
    return () => {
      alive = false
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener(OPS_READ_EVENT, onRead)
    }
  }, [load])

  const activity = useMemo(() => seatActivity(conversations, rooms), [conversations, rooms])
  const opsUnread = suppressKey !== null && suppressKey === opsToken ? 0 : ops.unread

  return {
    activity,
    ops: { ...ops, unread: opsUnread },
    refresh: load,
  }
}
